// tslint:disable:variable-name
import { get } from 'lodash'
import { first } from 'rxjs/operators'

import { Manifestation, getTranslatedString, SearchResult } from '@skinsoft/types'
import { preferredTerm } from '@skinsoft/selectors/thesaurus-term-selectors'
import { ThesaurusTermDataStore } from '@skinsoft/entity'

/**
 * Select work title first and fallback on manifestation title
 *  if first is null or empty
 * @param searchResult 
 */
export const title = (searchResult: SearchResult, locale: string) => {
    const type = itemType(searchResult)
    if (type && type.toLowerCase().includes('container')) {
        return getTranslatedString(get(searchResult, 'containerItemSchema.name'), locale)
    }

    return getTranslatedString(
        get(searchResult, 'identificationWorkSchema.workTitle.fullTitle')
            || get(searchResult, 'expressionSchema.referentWork.value')
            || get(searchResult, 'federatedSchema.titles[0]'),
        locale
    )
}

// item fields
export const creationDate = (searchResult: SearchResult) => {
    return get(searchResult, 'descriptionWorkSchema.creditsFirst.creationDatesFirst.date.txt') ??
    get(searchResult, 'descriptionWorkSchema.creditsFirst.creationDatesFirst.date.startInterval.dateTxt')
}
export const creationDates = (searchResult: SearchResult) => get(searchResult, 'federatedSchema.dates', [])
export const id = (searchResult: SearchResult) => get(searchResult, 'id')
export const itemType = (searchResult: SearchResult) => get(searchResult, 'entityType')
export const institution = (searchResult: SearchResult) => get(searchResult, 'institutionCreation');
export const inventoryNumber = (searchResult: SearchResult, locale: string) => get(searchResult, "identificationItemSchema.identifiersFirst.identifier.value");
export const media = (searchResult: SearchResult) => get(searchResult, 'associatedMedias.first.ref')
export const lastDateOfConditionReport = (searchResult: SearchResult) => get(searchResult, ''); // TODO
export const lastConditionReport = (searchResult: SearchResult) => get(searchResult, 'lastConditionReport');
export const realTimeLocation = (searchResult: SearchResult) => get(searchResult, 'locationItemSchema.realTimeLocation.realTimeLocation')
export const realTimeLocationId = (searchResult: SearchResult) => get(searchResult, 'locationItemSchema.realTimeLocation.realTimeLocation.ref')
export const permanentLocation = (searchResult: SearchResult) => get(searchResult, 'locationItemSchema.permanentLocation.permanentLocation')
export const futureLocation = (searchResult: SearchResult) => get(searchResult, 'locationItemSchema.futureLocation.futureLocation')

// work fields
export const authorId = (searchResult: SearchResult) => get(searchResult, 'federatedSchema.authors[0]')
export const credits = (searchResult: SearchResult) => {
    const alias = get(searchResult, 'descriptionWorkSchema.creditsFirst.creatorAuthorDirectorsFirst.alias.ref', null);
    const authorFunction = get(searchResult, 'descriptionWorkSchema.creditsFirst.creatorAuthorDirectorsFirst.functionFirst.ref', null);
    return { alias, authorFunction };
}

// manifestations fields
export const domain = (searchResult: SearchResult) => get(searchResult, 'descriptionSchema.domain[0].ref')
export const denomination = (searchResult: SearchResult) => get(searchResult, 'descriptionSchema.name.ref')
export const materialsAndTechnics = (searchResult: SearchResult, locale: string) => _materialsAndTechnics(searchResult, locale);;
export const measurements = (searchResult: SearchResult, locale: string) => _measurements(searchResult, locale);

// other fields to complete
export const weight = (searchResult: SearchResult) => "";

/**
 * Builds a textual summary of all materials and techniques
 * as an array of strings
 * @param manifestation 
 * @param locale 
 */
export const _materialsAndTechnics = async (manifestation: Manifestation, locale: string) => {
    const matAndTechs = get(manifestation, 'descriptionSchema.materialAndTechniques')
    const resolvedMAndT: string[] = [];
    if (matAndTechs) {
        for (const mt of matAndTechs) {
            const materials = mt.materials;
            const techniques = mt.techniques;

            let materialsSummary: string = "";
            let matIndex = 0;
            // Looping through materials
            if (materials) {
                for (const mat of materials) {
                    // material
                    let matTitle = null
                    if (mat.title) {
                        const matTerm = await ThesaurusTermDataStore.getInstance().fetchOne(mat.material.ref).pipe(first()).toPromise()
                        matTitle = getTranslatedString(preferredTerm(matTerm), locale)
                    }

                    // color
                    let colorTitle = null;
                    if (mat.color) {
                        if (mat.color.ref) {
                            const colorTerm = await ThesaurusTermDataStore.getInstance().fetchOne(mat.color.ref).pipe(first()).toPromise()
                            colorTitle = getTranslatedString(preferredTerm(colorTerm), locale)
                        } else {
                            colorTitle = getTranslatedString(mat.color, locale)
                        }
                    }
                    // precision
                    let precision = null;
                    if (mat.precision) {
                        precision = getTranslatedString(mat.precision, locale)
                    }

                    const matSummary: string = matTitle
                        + (colorTitle ? ` ${colorTitle}` : "")
                        + (precision ? ` (${precision})` : "")

                    if (matIndex > 0) {
                        materialsSummary += ", "
                    }

                    materialsSummary += matSummary;
                    matIndex++;
                }
            }
            let technicsSummary: string = "";
            let techIndex = 0;
            if (techniques) {
                for (const tech of techniques) {
                    // technique
                    const techTerm = await ThesaurusTermDataStore.getInstance().fetchOne(get(tech, 'technique.ref')).pipe(first()).toPromise()
                    const techTitle = getTranslatedString(preferredTerm(techTerm), locale)

                    // precision
                    let precision = null;
                    if (tech.precision) {
                        precision = getTranslatedString(tech.precision, locale)
                    }

                    const techSummary: string = techTitle
                        + (precision ? ` (${precision})` : "")

                    if (techIndex > 0) {
                        technicsSummary += ", "
                    }
                    technicsSummary += techSummary
                    techIndex++;
                }
            }
            resolvedMAndT.push(materialsSummary + (technicsSummary ? ` / ${technicsSummary}` : ""))
        }
    }
    return resolvedMAndT;
}

/**
 * Builds a summary of measurements as an array of strings
 * @param manifestation 
 * @param locale 
 */
export const _measurements = async (manifestation: Manifestation, locale: string) => {
    const measures = get(manifestation, 'descriptionSchema.measurements')
    const measuresSummary: string[] = []
    if (measures) {
        for (const measurementGroup of measures) {
            if (measurementGroup && measurementGroup.measures) {
                for (const measure of measurementGroup.measures) {
                    let measureType: string = null as any;
                    let measureValue: string = null as any;
                    let measureUnit: string = null as any;
                    if (measure.typeOfMeasure) {
                        const term = await ThesaurusTermDataStore.getInstance().fetchOne(measure.typeOfMeasure.ref).pipe(first()).toPromise()
                        measureType = getTranslatedString(preferredTerm(term), locale)
                    }
                    if (measure.value) {
                        measureValue = getTranslatedString(measure.value.ref, locale) as string
                    }
                    if (measure.unit) {
                        const term = await ThesaurusTermDataStore.getInstance().fetchOne(measure.unit.ref).pipe(first()).toPromise()
                        measureUnit = getTranslatedString(preferredTerm(term), locale)
                    }
                    if (measureValue) {
                        const measureSummary =
                            (measureType ? `${measureType} : ` : "")
                            + measureValue
                            + (measureUnit ? ` ${measureUnit}` : "")

                        measuresSummary.push(measureSummary);
                    }
                }
            }
        }
    }
    return measuresSummary;
}

export const pathsStrings: { [key: string]: string[] } = {
    realTimeLocationId: ['locationItemSchema.realTimeLocation.realTimeLocation.ref'],
}